/**
 * Reminder Backend API Service
 * This service handles communication with the Azure Functions backend for notifications and reminders.
 */

import axios from 'axios';

class ReminderBackendApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE;
    console.log('ReminderBackendApiService initialized with baseURL:', this.baseURL);
  }

  /**
   * Send a notification (POST /api/send-notification/post)
   * @param {string} name - Person's first name
   * @param {string} lastname - Person's last name  
   * @param {string} phone - Person's phone number
   * @returns {Promise} - API response
   */
  async sendNotification(name, lastname, phone) {
    try {
      console.log(`Sending notification for ${name} ${lastname} (${phone}) to ${this.baseURL}/send-notification/post`);
      
      const response = await axios.post(`${this.baseURL}/send-notification/post`, {
        name: String(name || ''),
        lastname: String(lastname || ''),
        phone: String(phone || '')
      });
      
      console.log(`Notification sent for ${name} ${lastname}:`, response.data);
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error sending notification:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    }
  }

  /**
   * Update a notification (PUT /api/notification/update)
   * @param {string} id - Notification ID
   * @param {object} updateFields - Fields to update
   * @returns {Promise} - API response
   */
  async updateNotification(id, updateFields) {
    try {
      const response = await axios.put(`${this.baseURL}/notification/update`, {
        id,
        updateFields
      });
      
      console.log(`Notification ${id} updated successfully`);
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error updating notification:', error);
      throw error;
    }
  }

  /**
   * Schedule a reminder (POST /api/schedule-reminder/post)
   * @param {string} campaignId - Campaign ID
   * @param {string} appointmentId - Appointment ID
   * @param {string} name - Person's first name
   * @param {string} lastname - Person's last name
   * @param {string} phone - Person's phone number
   * @returns {Promise} - API response with reminder details
   */
  async scheduleReminder(campaignId, appointmentId, name, lastname, phone) {
    try {
      console.log(`Scheduling reminder for ${name} ${lastname} (${phone})`);
      
      const response = await axios.post(`${this.baseURL}/schedule-reminder/post`, {
        campaignId: String(campaignId || ''),
        appointmentId: String(appointmentId || ''),
        name: String(name || ''),
        lastname: String(lastname || ''),
        phone: String(phone || '')
      });
      
      console.log(`Reminder scheduled for ${name} ${lastname} - ID: ${response.data.reminderId}`);
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error scheduling reminder:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    }
  }

  /**
   * Cancel a reminder (DELETE /api/reminder/cancel)
   * @param {string} campaignId - Campaign ID
   * @param {string} appointmentId - Appointment ID
   * @returns {Promise} - API response
   */
  async cancelReminder(campaignId, appointmentId) {
    try {
      const response = await axios.delete(`${this.baseURL}/reminder/cancel`, {
        params: {
          campaignId,
          appointmentId
        }
      });
      
      console.log(`Reminder cancelled for campaign ${campaignId}, appointment ${appointmentId}`);
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error cancelling reminder:', error);
      throw error;
    }
  }

  /**
   * Update a reminder (PUT /api/reminder/update)
   * @param {string} id - Reminder ID
   * @param {object} updateFields - Fields to update
   * @returns {Promise} - API response
   */
  async updateReminder(id, updateFields) {
    try {
      const response = await axios.put(`${this.baseURL}/reminder/update`, {
        id,
        updateFields
      });
      
      console.log(`Reminder ${id} updated successfully`);
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error updating reminder:', error);
      throw error;
    }
  }

  /**
   * Check for due reminders (GET /api/due-reminder/get)
   * @returns {Promise} - API response with processed reminders
   */
  async checkDueReminders() {
    try {
      console.log('Checking for due reminders...');
      
      const response = await axios.get(`${this.baseURL}/due-reminder/get`);
      
      console.log(`Checked due reminders - processed: ${response.data.processedReminders?.length || 0}`);
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error checking due reminders:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    }
  }

  /**
   * Convenience method to handle enrollment with automatic reminder scheduling
   * @param {string} campaignId - Campaign ID
   * @param {string} appointmentId - Appointment ID
   * @param {string} name - Person's first name
   * @param {string} lastname - Person's last name
   * @param {string} phone - Person's phone number
   * @param {boolean} enrolled - Whether the person is enrolled
   * @returns {Promise} - Results of operations
   */
  async handleEnrollmentWithReminder(campaignId, appointmentId, name, lastname, phone, enrolled) {
    const results = {};
    
    try {
      if (enrolled) {
        // Schedule reminder when enrolling
        results.reminder = await this.scheduleReminder(campaignId, appointmentId, name, lastname, phone);
        console.log(`Enrollment completed with reminder scheduled for ${name} ${lastname}`);
      } else {
        // Cancel reminder when unenrolling
        results.cancellation = await this.cancelReminder(campaignId, appointmentId);
        console.log(`Enrollment cancelled and reminder removed for ${name} ${lastname}`);
      }
      
      return results;
    } catch (error) {
      console.error('Error handling enrollment with reminder:', error);
      throw error;
    }
  }

  /**
   * Mark notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise} - API response
   */
  async markNotificationAsRead(notificationId) {
    return this.updateNotification(notificationId, { read: true });
  }

  /**
   * Mark notification as unread
   * @param {string} notificationId - Notification ID
   * @returns {Promise} - API response
   */
  async markNotificationAsUnread(notificationId) {
    return this.updateNotification(notificationId, { read: false });
  }

  /**
   * Send immediate follow-up notification
   * @param {string} name - Person's first name
   * @param {string} lastname - Person's last name
   * @param {string} phone - Person's phone number
   * @returns {Promise} - API response
   */
  async sendFollowUpNotification(name, lastname, phone) {
    return this.sendNotification(name, lastname, phone);
  }

  /**
   * Test backend connection
   * @returns {Promise} - Connection test results
   */
  async testConnection() {
    try {
      // Try to check due reminders as a connection test
      const response = await axios.get(`${this.baseURL}/due-reminder/get`);
      console.log('Backend connection successful:', response.status);
      return { connected: true, status: response.status };
    } catch (error) {
      console.error('Backend connection failed:', error.message);
      return { 
        connected: false, 
        error: error.message,
        baseURL: this.baseURL 
      };
    }
  }

  /**
   * Get backend configuration info
   * @returns {object} - Configuration details
   */
  getConfig() {
    return {
      baseURL: this.baseURL,
      endpoints: {
        sendNotification: `${this.baseURL}/send-notification/post`,
        updateNotification: `${this.baseURL}/notification/update`,
        deleteNotification: `${this.baseURL}/notification/delete`,
        deleteAllNotifications: `${this.baseURL}/notifications/delete-all`,
        scheduleReminder: `${this.baseURL}/schedule-reminder/post`,
        cancelReminder: `${this.baseURL}/reminder/cancel`,
        updateReminder: `${this.baseURL}/reminder/update`,
        checkDueReminders: `${this.baseURL}/due-reminder/get`,
        scheduleFollowUpReminder: `${this.baseURL}/schedule-followup-reminder/post`,
        checkDueFollowUpReminders: `${this.baseURL}/due-followup-reminder/get`
      }
    };
  }

  /**
   * Delete a single notification (DELETE /api/notification/delete)
   * @param {string} notificationId - Notification ID to delete
   * @returns {Promise} - API response
   */
  async deleteNotification(notificationId) {
    try {
      console.log(`Deleting notification ${notificationId}`);
      
      const response = await axios.delete(`${this.baseURL}/notification/delete`, {
        data: { notificationId }
      });
      
      console.log(`Notification ${notificationId} deleted successfully`);
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  /**
   * Delete all notifications (DELETE /api/notifications/delete-all)
   * @returns {Promise} - API response
   */
  async deleteAllNotifications() {
    try {
      console.log('Deleting all notifications');
      
      const response = await axios.delete(`${this.baseURL}/notifications/delete-all`);
      
      console.log(`All notifications deleted - count: ${response.data.deletedCount}`);
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      throw error;
    }
  }

  /**
   * Schedule a 2-week follow-up reminder (POST /api/schedule-followup-reminder/post)
   * @param {string} campaignId - Campaign ID
   * @param {string} appointmentId - Appointment ID
   * @param {string} name - Person's first name
   * @param {string} lastname - Person's last name
   * @param {string} phone - Person's phone number
   * @returns {Promise} - API response with follow-up reminder details
   */
  async scheduleFollowUpReminder(campaignId, appointmentId, name, lastname, phone) {
    try {
      console.log(`Scheduling 2-week follow-up reminder for ${name} ${lastname} (${phone})`);
      
      const response = await axios.post(`${this.baseURL}/schedule-followup-reminder/post`, {
        campaignId: String(campaignId || ''),
        appointmentId: String(appointmentId || ''),
        name: String(name || ''),
        lastname: String(lastname || ''),
        phone: String(phone || '')
      });
      
      console.log(`Follow-up reminder scheduled for ${name} ${lastname} - ID: ${response.data.followUpReminderId}`);
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error scheduling follow-up reminder:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    }
  }

  /**
   * Check for due 2-week follow-up reminders (GET /api/due-followup-reminder/get)
   * @returns {Promise} - API response with processed follow-up reminders
   */
  async checkDueFollowUpReminders() {
    try {
      console.log('Checking for due follow-up reminders...');
      
      const response = await axios.get(`${this.baseURL}/due-followup-reminder/get`);
      
      console.log(`Checked due follow-up reminders - processed: ${response.data.processedFollowUps?.length || 0}`);
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error checking due follow-up reminders:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error;
    }
  }
}

// Create and export singleton instance
const reminderBackendApiService = new ReminderBackendApiService();
export default reminderBackendApiService;
